import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { PlusCircle, Upload, X, GripVertical } from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogClose,
  DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { v4 as uuidv4 } from "uuid";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors,
  DragEndEvent 
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface Testimonial {
  id: number;
  name: string;
  content: string;
  location: string;
  image: string;
  created_at: string;
  display_order: number | null;
}

interface SortableItemProps {
  id: number;
  testimonial: Testimonial;
  onEdit: (testimonial: Testimonial) => void;
  onDelete: (testimonial: Testimonial) => void;
  columns: Array<{key: string, label: string, format?: (value: any, row: any) => React.ReactNode}>;
}

const SortableItem = ({ id, testimonial, onEdit, onDelete, columns }: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  
  return (
    <TableRow ref={setNodeRef} style={style} className="hover:bg-gray-50">
      <TableCell className="w-[40px]">
        <div className="flex items-center justify-center cursor-grab" {...attributes} {...listeners}>
          <GripVertical className="h-5 w-5 text-gray-400" />
        </div>
      </TableCell>
      
      {columns.map((column) => (
        <TableCell key={`${testimonial.id}-${column.key}`}>
          {column.format 
            ? column.format(testimonial[column.key as keyof Testimonial], testimonial) 
            : testimonial[column.key as keyof Testimonial]}
        </TableCell>
      ))}
      
      <TableCell>
        <div className="flex space-x-2">
          <Button variant="ghost" size="sm" onClick={() => onEdit(testimonial)}>
            Edit
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(testimonial)}>
            Delete
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const TestimonialsAdmin = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState<Partial<Testimonial>>({
    name: "",
    content: "",
    location: "",
    image: "",
    display_order: null
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [sortField, setSortField] = useState("display_order");
  const [sortOrder, setSortOrder] = useState("asc");
  const [isSaving, setIsSaving] = useState(false);
  const [bucketExists, setBucketExists] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const checkAndCreateBucket = async () => {
      try {
        const { data: buckets, error: bucketsError } = await supabase
          .storage
          .listBuckets();
        
        if (bucketsError) throw bucketsError;
        
        const bucketExists = buckets.some(bucket => bucket.name === 'testimonial-images');
        setBucketExists(bucketExists);
        
        if (!bucketExists) {
          console.warn("Testimonial images bucket does not exist. Please create it in Supabase dashboard.");
          toast({
            title: "Storage Configuration",
            description: "The 'testimonial-images' bucket does not exist. Please check your Supabase storage configuration.",
            variant: "destructive",
          });
        }
      } catch (error: any) {
        console.error("Error checking buckets:", error.message);
      }
    };
    
    checkAndCreateBucket();
    fetchTestimonials();
  }, [sortField, sortOrder]);

  const fetchTestimonials = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("testimonials")
        .select("*")
        .order(sortField, { ascending: sortOrder === "asc" });

      if (error) throw error;
      const testimonialsWithDisplayOrder = data?.map(testimonial => ({
        ...testimonial,
        display_order: testimonial.display_order || null
      })) || [];
      setTestimonials(testimonialsWithDisplayOrder);
    } catch (error: any) {
      toast({
        title: "Failed to load testimonials",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = testimonials.findIndex((item) => item.id === active.id);
      const newIndex = testimonials.findIndex((item) => item.id === over.id);
      const reorderedItems = arrayMove(testimonials, oldIndex, newIndex);
      setTestimonials(reorderedItems);
      
      try {
        setIsSaving(true);
        
        const updatedItems = reorderedItems.map((item, index) => ({
          id: item.id,
          display_order: index + 1
        }));
        
        for (const item of updatedItems) {
          const { error } = await supabase
            .from('testimonials')
            .update({ display_order: item.display_order })
            .eq('id', item.id);
            
          if (error) {
            console.error("Error updating item:", error);
            throw error;
          }
        }
        
        toast({
          title: "Order updated",
          description: "Testimonial display order has been saved",
        });
        
        await fetchTestimonials();
      } catch (error: any) {
        toast({
          title: "Failed to update order",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCurrentTestimonial(prev => ({ ...prev, [name]: value }));
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/i)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a valid image file (JPEG, PNG, GIF, WEBP)",
        variant: "destructive",
      });
      return;
    }

    setImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
  };

  const uploadImage = async (file: File): Promise<string> => {
    try {
      setIsUploading(true);
      
      if (!bucketExists) {
        throw new Error("Storage bucket 'testimonial-images' does not exist. Please configure storage in Supabase dashboard.");
      }
      
      console.log("Starting testimonial image upload:", file.name, file.size, "bytes");
      
      if (file.size > 2 * 1024 * 1024) {
        throw new Error("File size too large. Please upload an image smaller than 2MB.");
      }
      
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error(`Invalid file type. Allowed types: ${allowedTypes.join(', ')}`);
      }
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("You must be logged in to upload images.");
      }
      
      const fileExt = file.name.split('.').pop();
      let filePath = `${uuidv4()}.${fileExt}`;

      console.log("Uploading to testimonial-images bucket, filepath:", filePath);

      const { data: existingFiles } = await supabase.storage
        .from('testimonial-images')
        .list();
        
      const fileExists = existingFiles?.some(f => f.name === filePath);
      
      if (fileExists) {
        const newFileName = `${uuidv4()}-${Date.now()}.${fileExt}`;
        filePath = newFileName;
      }

      const { error: uploadError, data } = await supabase.storage
        .from('testimonial-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        console.error("Supabase upload error details:", uploadError);
        throw uploadError;
      }

      console.log("Upload successful, getting public URL");
      const { data: publicUrlData } = supabase.storage
        .from('testimonial-images')
        .getPublicUrl(filePath);

      console.log("Public URL acquired:", publicUrlData.publicUrl);
      return publicUrlData.publicUrl;
    } catch (error: any) {
      console.error("Error uploading testimonial image:", error.message);
      throw new Error(`Error uploading image: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const clearImageSelection = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!currentTestimonial.name || !currentTestimonial.content || !currentTestimonial.location) {
        toast({
          title: "Missing fields",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }
      
      let imageUrl = currentTestimonial.image || '';
      
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      } else if (!imageUrl && !isEditing) {
        toast({
          title: "Missing image",
          description: "Please upload an image for the testimonial",
          variant: "destructive",
        });
        return;
      }
      
      if (isEditing && currentTestimonial.id) {
        const { error } = await supabase
          .from("testimonials")
          .update({
            name: currentTestimonial.name,
            content: currentTestimonial.content,
            location: currentTestimonial.location,
            image: imageUrl,
          })
          .eq("id", currentTestimonial.id);
          
        if (error) throw error;
        
        toast({
          title: "Testimonial updated",
          description: "The testimonial has been successfully updated",
        });
      } else {
        const { data: maxOrderData, error: maxOrderError } = await supabase
          .from("testimonials")
          .select("display_order")
          .order("display_order", { ascending: false })
          .limit(1);
          
        if (maxOrderError) throw maxOrderError;
        
        const maxOrder = maxOrderData && maxOrderData.length > 0 && maxOrderData[0]?.display_order 
          ? maxOrderData[0].display_order 
          : 0;
        
        const { error } = await supabase
          .from("testimonials")
          .insert({
            name: currentTestimonial.name,
            content: currentTestimonial.content,
            location: currentTestimonial.location,
            image: imageUrl,
            display_order: maxOrder + 1
          });
          
        if (error) throw error;
        
        toast({
          title: "Testimonial created",
          description: "A new testimonial has been successfully created",
        });
      }
      
      setDialogOpen(false);
      resetForm();
      await fetchTestimonials();
    } catch (error: any) {
      toast({
        title: isEditing ? "Failed to update testimonial" : "Failed to create testimonial",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (testimonial: Testimonial) => {
    if (!confirm("Are you sure you want to delete this testimonial?")) return;

    try {
      if (testimonial.image && testimonial.image.includes('testimonial-images')) {
        const imageUrl = new URL(testimonial.image);
        const pathParts = imageUrl.pathname.split('/');
        const fileName = pathParts[pathParts.length - 1];
        
        const { error: storageError } = await supabase.storage
          .from('testimonial-images')
          .remove([fileName]);
          
        if (storageError) {
          console.error("Error deleting image:", storageError);
          // Continue with deletion even if image removal fails
        }
      }

      const { error } = await supabase
        .from("testimonials")
        .delete()
        .eq("id", testimonial.id);

      if (error) throw error;

      toast({
        title: "Testimonial deleted",
        description: "The testimonial has been successfully deleted",
      });

      await fetchTestimonials();
    } catch (error: any) {
      toast({
        title: "Failed to delete testimonial",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (testimonial: Testimonial) => {
    setCurrentTestimonial(testimonial);
    setIsEditing(true);
    setDialogOpen(true);
    
    if (testimonial.image) {
      setImagePreview(testimonial.image);
    } else {
      setImagePreview(null);
    }
    setImageFile(null);
  };

  const resetForm = () => {
    setCurrentTestimonial({
      name: "",
      content: "",
      location: "",
      image: "",
      display_order: null
    });
    setIsEditing(false);
    clearImageSelection();
  };

  const handleAddNew = () => {
    resetForm();
    setDialogOpen(true);
  };

  const columns = [
    {
      key: "name",
      label: "Name",
    },
    {
      key: "location",
      label: "Location",
    },
    {
      key: "content",
      label: "Content",
      format: (value: string) => 
        value.length > 100 ? `${value.substring(0, 100)}...` : value,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Testimonials</h2>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleAddNew}
            className="flex items-center gap-2"
          >
            <PlusCircle className="h-4 w-4" /> Add Testimonial
          </Button>
        </div>
      </div>
      
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Sort by:</span>
          <Select
            value={sortField}
            onValueChange={setSortField}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Sort field" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="display_order">Custom Order</SelectItem>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="created_at">Creation Date</SelectItem>
              <SelectItem value="location">Location</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Order:</span>
          <Select
            value={sortOrder}
            onValueChange={setSortOrder}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Order" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">Ascending</SelectItem>
              <SelectItem value="desc">Descending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-pulse">Loading testimonials...</div>
        </div>
      ) : testimonials.length === 0 ? (
        <div className="text-center py-8 border rounded-md bg-gray-50">
          <p className="text-gray-500">No testimonials available yet.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddNew}
            className="mt-4"
          >
            <PlusCircle className="h-4 w-4 mr-2" /> Add Your First Testimonial
          </Button>
        </div>
      ) : (
        <div className="border rounded-md">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">Order</TableHead>
                  {columns.map((column) => (
                    <TableHead key={column.key}>{column.label}</TableHead>
                  ))}
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <SortableContext
                items={testimonials.map(t => t.id)}
                strategy={verticalListSortingStrategy}
              >
                <TableBody>
                  {testimonials.map((testimonial) => (
                    <SortableItem
                      key={testimonial.id}
                      id={testimonial.id}
                      testimonial={testimonial}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      columns={columns}
                    />
                  ))}
                </TableBody>
              </SortableContext>
            </Table>
          </DndContext>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Edit Testimonial" : "Add New Testimonial"}
            </DialogTitle>
            <DialogDescription>
              All fields are required. Upload an image for the testimonial.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                name="name"
                value={currentTestimonial.name}
                onChange={handleInputChange}
                placeholder="Client name"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location/Position</Label>
              <Input
                id="location"
                name="location"
                value={currentTestimonial.location}
                onChange={handleInputChange}
                placeholder="e.g., CTO at Company X, New York"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="image">Testimonial Image</Label>
              {imagePreview ? (
                <div className="relative w-24 h-24 rounded-full overflow-hidden mx-auto border border-gray-200">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={clearImageSelection}
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                    title="Remove image"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="flex justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={triggerFileInput}
                    className="flex flex-col items-center justify-center w-24 h-24 rounded-full border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100"
                  >
                    <Upload className="w-6 h-6 text-gray-400" />
                    <span className="text-xs text-gray-500 mt-1">Upload</span>
                  </Button>
                  <input 
                    type="file"
                    className="hidden"
                    onChange={handleImageChange}
                    accept="image/*"
                    ref={fileInputRef}
                  />
                </div>
              )}
              <p className="text-xs text-gray-500 text-center mt-2">
                Upload a profile image (JPEG, PNG, GIF, WEBP)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="content">Testimonial Content</Label>
              <Textarea
                id="content"
                name="content"
                value={currentTestimonial.content}
                onChange={handleInputChange}
                placeholder="What the client said about your service"
                required
                rows={5}
              />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">Cancel</Button>
              </DialogClose>
              <Button type="submit" disabled={isUploading}>
                {isUploading ? "Uploading..." : (isEditing ? "Update" : "Create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {isSaving && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-md shadow-lg">
            <div className="animate-spin h-8 w-8 border-4 border-brand-green border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-2 text-center text-gray-700">Saving order...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsAdmin;
